// Stepper — a compact horizontal pipeline of a request's state machine.
//
// Used by the account-request and recharge-request queues to show where a
// request sits (e.g. requested -> assigned -> in_progress -> ready -> shared).
// The current step is brand-tinted, completed steps are filled, future steps are
// muted. Rejected is surfaced by the caller (a danger pill), not as a step.

import { cn } from "@/lib/utils";

export interface Step {
  key: string;
  label: string;
}

export function Stepper({
  steps,
  current,
  className,
}: {
  steps: Step[];
  // Index of the active step (0-based). Steps before it are "done".
  current: number;
  className?: string;
}) {
  return (
    <ol
      data-slot="stepper"
      className={cn("flex items-center gap-1", className)}
    >
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={s.key} className="flex items-center gap-1">
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.7rem] font-medium whitespace-nowrap",
                active && "bg-nx-brand-soft text-nx-brand",
                done && "text-nx-credit",
                !done && !active && "text-nx-text-3",
              )}
            >
              <span
                className={cn(
                  "size-1.5 rounded-full",
                  active && "bg-nx-brand",
                  done && "bg-nx-credit",
                  !done && !active && "bg-nx-na",
                )}
              />
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <span
                className={cn(
                  "h-px w-3",
                  done ? "bg-nx-credit" : "bg-nx-border",
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
